// OSPROD-191: PERSONAL unread task-events counter for the Tasks rail badge.
// This is distinct from `taskCountsStore` (which counts a board's UNASSIGNED
// non-done tickets, per-board, for the board-list counter). Here the badge is a
// per-user "events for me" count — assigned to me / new reply on my task, etc —
// backed by the notification platform (unread InAppNotification rows in the
// item.* / board.* categories).
//
// QA round 2 (2026-07-01): the badge no longer clears wholesale on opening the
// Tasks module. Every task bell row carries `sourceKey = taskboard:<boardId>`,
// so events clear PER BOARD when the user actually opens that board
// (markBoardSeen, called from the taskboards BoardView) — and the board list
// shows which board still has unseen activity.
import { create } from 'zustand'
import { notificationApi } from '../services/api'

interface TaskInboxState {
  unread: number
  fetch: () => Promise<void>
  markBoardSeen: (boardId: string) => Promise<void>
}

let inFlight: Promise<void> | null = null
let debounceTimer: ReturnType<typeof setTimeout> | null = null

export const useTaskInboxStore = create<TaskInboxState>((set) => ({
  unread: 0,
  fetch: async () => {
    if (inFlight) return inFlight
    inFlight = notificationApi
      .tasksUnreadCount()
      .then((r) => set({ unread: r.data.count ?? 0 }))
      .catch(() => { /* keep last-known on a transient error */ })
      .finally(() => { inFlight = null })
    return inFlight
  },
  // Opening a board marks that board's task events read; the server emits
  // `notification:updated` which reconciles this and any other device.
  markBoardSeen: async (boardId: string) => {
    try {
      await notificationApi.tasksMarkBoardRead(boardId)
    } catch { /* best-effort */ }
    void useTaskInboxStore.getState().fetch()
  },
}))

// A burst of task notifications (e.g. several assignments) should coalesce into
// a single refetch of the count.
export function scheduleTaskInboxRefetch(): void {
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => { void useTaskInboxStore.getState().fetch() }, 400)
}
